import React, { useState } from 'react';
import { Alert, Box, Stack } from '@mui/material';
import PaymentRequestContent from './PaymentRequestContent';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectParams,
  selectPaymentRequests,
  selectRemainingBalanceDue,
  selectSettings,
  selectStatus,
} from '../../../redux/selectors/checkoutSelectors';
import { AetherPaymentRequest } from '../../../models/Payment';
import _, { uniqueId } from 'lodash';
import { LoadingButton } from '@mui/lab';
import { getError } from '../../../helpers/checkout';
import { CheckoutStatus } from '../../../models/CheckoutSession';
import {
  addPaymentRequest,
  editPaymentRequest,
  updatePaymentRequest,
} from '../../../redux/reducers/checkoutReducer';
import OtpDialog from './OtpDialog';
import { OtpRequest } from '../../../models/Otp';
import WholePageSpinner from '../../../common/WholePageSpinner';
import usePlaceOrder from '../usePlaceOrder';
import PaymentSummaryCard from './PaymentSummaryCard';

export default function PaymentActiveContent() {
  const dispatch = useAppDispatch();
  const params = useAppSelector(selectParams);
  const settings = useAppSelector((state) => selectSettings(state, params?.orderType));
  const status = useAppSelector((state) => selectStatus(state, params));
  const paymentRequests = useAppSelector(selectPaymentRequests);
  const remainingBalance = useAppSelector((state) =>
    selectRemainingBalanceDue(state, params, null),
  );
  const [selected, setSelected] = useState<number | null>(null);
  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [newRequestKey, setNewRequestKey] = useState(uniqueId());

  const {
    placeOrder,
    loadingPlaceOrder,
    submitPaymentsLoading,
    submitPaymentsError,
    submitOrderLoading,
    submitOrderError,
    paymentErrors,
  } = usePlaceOrder({ selected, setSelected, setOpenOtpDialog });

  const onSubmit = ({ index, data }: { index: number | null; data: AetherPaymentRequest }) => {
    if (index === null) {
      dispatch(addPaymentRequest(data));
    } else {
      dispatch(updatePaymentRequest({ index, request: data }));
    }
    setNewRequestKey(uniqueId());
    setSelected(null);
  };

  const handleSelect = (index: number | null) => () => {
    setSelected(index);
    if (index !== null) {
      dispatch(editPaymentRequest({ index }));
    }
  };

  const handleOtpDialogContinue = (otpReq: OtpRequest) => {
    setOpenOtpDialog(false);
    placeOrder(otpReq);
  };

  const handleOtpDialogClose = () => {
    setOpenOtpDialog(false);
  };

  const handlePlaceOrder = () => {
    const needOtp = paymentRequests.some((request) => request.otpEnabled);
    if (needOtp) {
      setOpenOtpDialog(true);
    } else {
      placeOrder();
    }
  };

  return (
    <Box>
      {(submitPaymentsLoading || loadingPlaceOrder || submitOrderLoading) && (
        <WholePageSpinner text="Submitting Order" warningText="Please do not refresh the page" />
      )}
      <OtpDialog
        open={openOtpDialog}
        onClose={handleOtpDialogClose}
        onContinue={handleOtpDialogContinue}
      />
      <Stack direction={'column'} spacing={2}>
        {status !== CheckoutStatus.PENDING_SUBMISSION && settings?.paymentInstructionsEnabled && (
          <Alert severity="info">{settings.paymentInstructionsText}</Alert>
        )}
        {paymentRequests.map((_, index) =>
          selected === index ? (
            <PaymentRequestContent
              key={index}
              index={index}
              errorMessage={paymentErrors[index]}
              onEdit={handleSelect(index)}
              onSubmit={onSubmit}
            />
          ) : (
            <PaymentSummaryCard
              key={index}
              payment={paymentRequests[index]}
              onEdit={handleSelect(index)}
            />
          ),
        )}
        {selected === null && remainingBalance > 0 && (
          <PaymentRequestContent
            key={newRequestKey}
            index={null}
            onEdit={handleSelect(null)}
            onSubmit={onSubmit}
          />
        )}
        <LoadingButton
          data-testid="place-order-btn"
          variant="contained"
          size={'large'}
          disabled={selected !== null || remainingBalance !== 0}
          loading={submitPaymentsLoading || submitOrderLoading || loadingPlaceOrder}
          onClick={handlePlaceOrder}
        >
          {settings.submitButtonLabel}
        </LoadingButton>
        {submitPaymentsError && (
          <Alert severity="error">Error Processing Payments: {getError(submitPaymentsError)}</Alert>
        )}
        {submitOrderError && (
          <Alert severity="error">Error Submitting Order: {getError(submitOrderError)}</Alert>
        )}
      </Stack>
    </Box>
  );
}
